import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { FaSync, FaCalendar } from 'react-icons/fa';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface ProfitHistoryChartProps {
  className?: string;
  /** null / undefined = show consolidated "All Accounts" view */
  accountId?: number | null;
  /** Display label used in the chart title */
  accountName?: string;
}

const ProfitHistoryChart: React.FC<ProfitHistoryChartProps> = ({
  className = '',
  accountId,
  accountName,
}) => {
  const adminApi = useAdminApiService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [period, setPeriod] = useState('7'); // Default to last 7 days
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // When true, always fetches consolidated (all-accounts) data regardless of accountId prop
  const [showConsolidated, setShowConsolidated] = useState(false);

  // ===== Complete P&L Summary (daily history by date) =====
  const [pnlHistory, setPnlHistory] = useState<any[]>([]);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlError, setPnlError] = useState<string | null>(null);

  const formatCurrency = (amount: number | string): string => {
    if (amount === null || amount === undefined) return '$0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Calculate date range based on selected period
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    if (period === 'custom') {
      return { start: startDate, end: endDate };
    }
    
    const days = parseInt(period);
    start.setDate(end.getDate() - days);
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const loadSnapshots = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { start, end } = getDateRange();
      
      if (!start || !end) {
        setError('Please select both start and end dates');
        setLoading(false);
        return;
      }
      
      // null → consolidated (all accounts); specific ID → per-account
      const effectiveAccountId = showConsolidated ? null : accountId;
      const response = await adminApi.getKucoinSnapshots(start, end, effectiveAccountId);

      if (response.success && response.data) {
        setSnapshots(response.data);
      } else {
        setError(response.error || 'Failed to load profit history');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profit history');
    } finally {
      setLoading(false);
    }
  };

  // Load daily P&L history (for Complete P&L Summary chart with dates)
  const loadPnlHistory = async () => {
    setPnlLoading(true);
    setPnlError(null);

    try {
      // Use the same period as the profit history chart
      const days = period === 'custom' ? 30 : parseInt(period);
      const response = await adminApi.getTradingStrategyPnlHistory(days);

      if (response.success && response.data) {
        setPnlHistory(response.data || []);
      } else {
        setPnlError(response.error || 'Failed to load P&L history');
      }
    } catch (err: any) {
      setPnlError(err.message || 'Failed to load P&L history');
      console.error('Error loading trading strategy P&L history:', err);
    } finally {
      setPnlLoading(false);
    }
  };

  // Reload whenever period, account, or consolidated toggle changes
  useEffect(() => {
    if (period !== 'custom') {
      loadSnapshots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, accountId, showConsolidated]);

  // Load P&L history when component mounts or period changes
  useEffect(() => {
    loadPnlHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Prepare chart data
  const chartData = {
    labels: snapshots.map(s => new Date(s.snapshotDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Current Profit',
        data: snapshots.map(s => parseFloat(s.currentProfit)),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Account Equity',
        data: snapshots.map(s => parseFloat(s.accountEquity)),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Total Funded',
        data: snapshots.map(s => parseFloat(s.totalFunded || 0)),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.1)',
        borderDash: [5, 5],
        fill: false,
        tension: 0,
        yAxisID: 'y',
      },
      // ROI is only shown for the consolidated view (funding is tracked centrally).
      ...((showConsolidated || accountId == null) ? [{
        label: 'ROI %',
        data: snapshots.map(s => parseFloat(s.roiPercentage || 0)),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      }] : [])
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: showConsolidated
          ? 'Profit History & ROI — All Accounts (Consolidated)'
          : `Profit History — ${accountName || (accountId != null ? 'Account #' + accountId : 'All Accounts')}`,
        font: {
          size: 16,
          weight: 'bold' as const
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              // Format based on dataset (ROI% vs USD values)
              if (label.includes('ROI')) {
                label += context.parsed.y.toFixed(2) + '%';
              } else {
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(context.parsed.y);
              }
            }
            return label;
          }
        }
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x' as const,
        },
        pan: {
          enabled: true,
          mode: 'x' as const,
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'USD Value',
          font: {
            size: 12,
            weight: 'bold' as const
          }
        },
        ticks: {
          callback: function(value: any) {
            return '$' + value.toFixed(2);
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'ROI %',
          font: {
            size: 12,
            weight: 'bold' as const
          }
        },
        ticks: {
          callback: function(value: any) {
            return value.toFixed(2) + '%';
          }
        },
        grid: {
          drawOnChartArea: false,
        },
      }
    }
  };

  // ===== Complete P&L Summary Chart (daily history by date) =====
  const pnlChartData = {
    labels: pnlHistory.map((row) => {
      const dateStr = row.trade_date || row.tradeDate || row.date;
      if (dateStr) {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return '';
    }).filter(label => label !== ''),
    datasets: [
      {
        label: 'Net P&L (Realized + Unrealized - Fees)',
        data: pnlHistory.map((row) => {
          const netPnl = row.cumulative_net_pnl ?? row.cumulativeNetPnl ?? row.cumulative_net_pnl ?? 0;
          return typeof netPnl === 'number' ? netPnl : parseFloat(netPnl) || 0;
        }),
        borderColor: 'rgb(22, 163, 74)',
        backgroundColor: 'rgba(22, 163, 74, 0.2)',
        fill: true,
        tension: 0.35,
        yAxisID: 'y',
      },
      {
        label: 'Realized P&L (Closed Trades)',
        data: pnlHistory.map((row) => {
          const realizedPnl = row.cumulative_realized_pnl ?? row.cumulativeRealizedPnl ?? row.cumulative_realized_pnl ?? 0;
          return typeof realizedPnl === 'number' ? realizedPnl : parseFloat(realizedPnl) || 0;
        }),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.18)',
        fill: true,
        tension: 0.35,
        yAxisID: 'y',
      },
    ],
  };

  const pnlChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Complete P&L Summary by Date',
        font: {
          size: 15,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        display: true,
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'P&L (USD)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
        ticks: {
          callback: function (value: any) {
            return '$' + Number(value).toFixed(2);
          },
        },
      },
    },
  };

  return (
    <div className={`profit-history-chart ${className}`} style={{
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      marginTop: '16px'
    }}>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <FaCalendar style={{ color: '#086afb', fontSize: '18px' }} />
        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Profit History</h4>
        
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Consolidated toggle — only show when viewing a specific account */}
          {accountId != null && (
            <button
              onClick={() => setShowConsolidated(prev => !prev)}
              title={showConsolidated ? 'Showing all accounts — click to show this account only' : 'Click to show all accounts consolidated'}
              style={{
                padding: '6px 14px',
                background: showConsolidated ? '#086afb' : '#f3f4f6',
                color: showConsolidated ? 'white' : '#374151',
                border: `1px solid ${showConsolidated ? '#086afb' : '#e5e7eb'}`,
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              📊 {showConsolidated ? '✓ Consolidated' : 'Show Consolidated'}
            </button>
          )}

          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {period === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                onClick={loadSnapshots}
                disabled={loading}
                style={{
                  padding: '6px 16px',
                  background: '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FaSync className={loading ? 'spinning' : ''} />
                View
              </button>
            </>
          )}
          
          <button
            onClick={() => {
              loadSnapshots();
              loadPnlHistory();
            }}
            disabled={loading}
            style={{
              padding: '6px 12px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Refresh data"
          >
            <FaSync className={loading ? 'spinning' : ''} style={{ fontSize: '14px' }} />
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '6px',
          color: '#c33',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666'
        }}>
          <FaSync className="spinning" style={{ fontSize: '24px', marginBottom: '12px' }} />
          <div>Loading profit history...</div>
        </div>
      )}

      {!loading && !error && snapshots.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666'
        }}>
          <p>No historical data available for the selected period.</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
            Data is captured hourly. Please check back later or select a different date range.
          </p>
        </div>
      )}

      {!loading && !error && snapshots.length > 0 && (
        <div style={{ height: '400px', marginBottom: '24px' }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      {/* Complete P&L Summary graph by date */}
      {!pnlLoading && !pnlError && pnlHistory.length > 0 && (
        <div style={{ height: '360px', marginTop: '8px' }}>
          <Line data={pnlChartData} options={pnlChartOptions} />
        </div>
      )}

      {pnlLoading && (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            color: '#666',
          }}
        >
          <FaSync className="spinning" style={{ fontSize: '20px', marginBottom: '8px' }} />
          <div>Loading Complete P&L Summary...</div>
        </div>
      )}

      {!pnlLoading && pnlError && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#b91c1c',
            fontSize: '13px',
          }}
        >
          {pnlError}
        </div>
      )}

      {!pnlLoading && !pnlError && pnlHistory.length === 0 && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#6b7280',
          }}
        >
          No P&L history data available for the selected period.
        </div>
      )}
    </div>
  );
};

export default ProfitHistoryChart;

